import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { moduloAtivo } from '../config/menuModulos';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  ClipboardCheck, 
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  FileText,
  Package,
  Maximize2,
  X,
  Video
} from 'lucide-react';
import { formatQuantidadeComUnidade } from '../utils/numberFormat';

const QualidadeDashboard = () => {
  const { companyId, companyModulos } = useAuth();
  const navigate = useNavigate();
  const [recebimentosPendentes, setRecebimentosPendentes] = useState([]);
  const [recebimentosAprovados, setRecebimentosAprovados] = useState([]);
  const [recebimentosRecusados, setRecebimentosRecusados] = useState([]);
  const [recebimentosAprovadosDiretamente, setRecebimentosAprovadosDiretamente] = useState([]);
  const [recebimentosTodos, setRecebimentosTodos] = useState([]); // Todos os recebimentos carregados
  const [filtroAtivo, setFiltroAtivo] = useState('pendentes'); // 'pendentes', 'aprovados', 'recusados', 'aprovados_diretamente'
  const [loading, setLoading] = useState(true);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [selectedReceiving, setSelectedReceiving] = useState(null);
  const [decisaoQualidade, setDecisaoQualidade] = useState('');
  const [observacoesQualidade, setObservacoesQualidade] = useState('');
  const [processando, setProcessando] = useState(false);
  const [viewingMedia, setViewingMedia] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  
  // Abrir modal de visualização de mídia
  const openMediaViewer = (media, type) => {
    // ✅ CORREÇÃO: Normalizar mídia para garantir que sempre tenha uma URL
    let normalizedMedia;
    if (typeof media === 'string') {
      // Se for string (URL direta do Firestore), criar objeto com url
      normalizedMedia = { url: media };
    } else if (media && typeof media === 'object') {
      // Se for objeto, usar diretamente mas garantir que tenha url
      normalizedMedia = { ...media };
      if (!normalizedMedia.url && !normalizedMedia.dataUrl && !normalizedMedia.thumbnailUrl) {
        // Se não tiver nenhuma URL, tentar usar o próprio valor como string
        normalizedMedia.url = String(media);
      }
    } else {
      // Fallback: criar objeto básico
      normalizedMedia = { url: String(media || '') };
    }
    
    setViewingMedia({ ...normalizedMedia, type });
    setShowMediaModal(true);
  };
  
  // ✅ NOVO: Formatar lotes para exibição
  const formatarLotesParaExibicao = (lotes) => {
    if (!lotes || lotes.length === 0) return 'N/A';
    if (Array.isArray(lotes)) {
      if (lotes.length === 1) return lotes[0];
      if (lotes.length <= 5) return lotes.join(', ');
      return `${lotes.slice(0, 3).join(', ')}... (+${lotes.length - 3} lotes)`;
    }
    // Compatibilidade com dados antigos (string)
    return String(lotes);
  };

  useEffect(() => {
    if (companyId && companyId !== 'default') {
      loadRecebimentos();
    }
  }, [companyId]);

  // ✅ NOVO: Carregar todos os recebimentos e separar por status
  const loadRecebimentos = async () => {
    try {
      setLoading(true);
      const listReceivings = httpsCallable(functions, 'listReceivings');
      
      const result = await listReceivings({
        empresa_id: companyId,
        page: 1,
        limit: 1000 // Aumentar limite para pegar todos
      });

      if (result.data?.success) {
        const todosRecebimentos = result.data.data.recebimentos || [];
        setRecebimentosTodos(todosRecebimentos);
        
        // Separar por status
        const pendentes = todosRecebimentos.filter(
          r => r.decisao_final === 'em_analise_qualidade'
        );
        const aprovados = todosRecebimentos.filter(
          r => r.decisao_final === 'aprovado_qualidade' || r.decisao_qualidade?.decisao === 'aprovado_qualidade'
        );
        const recusados = todosRecebimentos.filter(
          r => r.decisao_final === 'rejeitado_qualidade' || r.decisao_qualidade?.decisao === 'rejeitado_qualidade'
        );
        // ✅ NOVO: Recebimentos aprovados diretamente sem passar pela qualidade
        const aprovadosDiretamente = todosRecebimentos.filter(
          r => r.decisao_final === 'aceitar' && !r.decisao_qualidade && r.decisao_final !== 'aprovado_qualidade'
        );
        
        setRecebimentosPendentes(pendentes);
        setRecebimentosAprovados(aprovados);
        setRecebimentosRecusados(recusados);
        setRecebimentosAprovadosDiretamente(aprovadosDiretamente);
      }
    } catch (error) {
      console.error('Erro ao carregar recebimentos:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOVO: Obter recebimentos filtrados baseado no filtro ativo
  const getRecebimentosFiltrados = () => {
    switch (filtroAtivo) {
      case 'aprovados':
        return recebimentosAprovados;
      case 'recusados':
        return recebimentosRecusados;
      case 'aprovados_diretamente':
        return recebimentosAprovadosDiretamente;
      case 'pendentes':
      default:
        return recebimentosPendentes;
    }
  };

  // ✅ NOVO: Obter título da lista baseado no filtro
  const getTituloLista = () => {
    switch (filtroAtivo) {
      case 'aprovados':
        return 'Recebimentos Aprovados pela Qualidade';
      case 'recusados':
        return 'Recebimentos Recusados pela Qualidade';
      case 'aprovados_diretamente':
        return 'Recebimentos Aprovados Diretamente (sem análise da qualidade)';
      case 'pendentes':
      default:
        return 'Recebimentos Aguardando Análise da Qualidade';
    }
  };

  const handleAbrirDecisao = (recebimento) => {
    setSelectedReceiving(recebimento);
    // ✅ CORREÇÃO: Se já existe uma decisão, preencher com ela
    // Verificar decisao_qualidade.decisao ou decisao_final
    // ✅ NOVO: Verificar também se foi aprovado diretamente (aceitar sem decisao_qualidade)
    const decisaoExistente = recebimento.decisao_qualidade?.decisao || 
                             (recebimento.decisao_final === 'aprovado_qualidade' ? 'aprovado_qualidade' : 
                              recebimento.decisao_final === 'rejeitado_qualidade' ? 'rejeitado_qualidade' : 
                              (recebimento.decisao_final === 'aceitar' && !recebimento.decisao_qualidade) ? 'aprovado_diretamente' : null);
    
    if (decisaoExistente && decisaoExistente !== 'aprovado_diretamente') {
      setDecisaoQualidade(decisaoExistente);
    } else {
      setDecisaoQualidade('');
    }
    setObservacoesQualidade(recebimento.decisao_qualidade?.observacoes || recebimento.observacoes_qualidade || '');
    setShowDecisionModal(true);
  };

  const handleDecidirQualidade = async () => {
    if (!decisaoQualidade) {
      alert('Selecione uma decisão (Aprovar ou Rejeitar)');
      return;
    }

    try {
      setProcessando(true);
      
      // Chamar função para atualizar decisão da qualidade
      const updateReceivingQuality = httpsCallable(functions, 'updateReceivingQualityDecision');
      
      const result = await updateReceivingQuality({
        recebimento_id: selectedReceiving.id,
        empresa_id: companyId,
        decisao_qualidade: decisaoQualidade,
        observacoes_qualidade: observacoesQualidade
      });

      if (result.data?.success) {
        alert('Decisão registrada com sucesso!');
        setShowDecisionModal(false);
        setSelectedReceiving(null);
        await loadRecebimentos(); // ✅ CORREÇÃO: Recarregar todos os recebimentos
      } else {
        throw new Error(result.data?.message || 'Erro ao registrar decisão');
      }
    } catch (error) {
      console.error('Erro ao decidir:', error);
      alert('Erro ao registrar decisão: ' + error.message);
    } finally {
      setProcessando(false);
    }
  };

  const formatDate = (date) => {
    try {
      if (date?._seconds) {
        return new Date(date._seconds * 1000).toLocaleDateString('pt-BR');
      } else if (date) {
        const d = new Date(date);
        return !isNaN(d.getTime()) ? d.toLocaleDateString('pt-BR') : 'N/A';
      }
      return 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6 text-white">
          <h1 className="text-2xl font-bold">Dashboard - Qualidade</h1>
          <p className="text-blue-100 mt-1">
            Análise e aprovação de recebimentos pendentes
          </p>
          {moduloAtivo(companyModulos || {}, 'monitoramento') && (
            <button
              type="button"
              onClick={() => navigate('/app/monitoramento')}
              className="mt-4 inline-flex items-center rounded-lg bg-white/20 px-4 py-2 text-sm font-medium hover:bg-white/30"
            >
              Abrir Monitoramento
            </button>
          )}
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Card Pendentes */}
          <div 
            onClick={() => setFiltroAtivo('pendentes')}
            className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
              filtroAtivo === 'pendentes' 
                ? 'border-yellow-500 bg-yellow-50' 
                : 'border-gray-200 hover:border-yellow-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pendentes de Análise</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{recebimentosPendentes.length}</p>
              </div>
              <div className={`p-3 rounded-lg ${
                filtroAtivo === 'pendentes' ? 'bg-yellow-500' : 'bg-yellow-100'
              }`}>
                <Clock className={`h-6 w-6 ${
                  filtroAtivo === 'pendentes' ? 'text-white' : 'text-yellow-600'
                }`} />
              </div>
            </div>
          </div>

          {/* Card Aprovados */}
          <div 
            onClick={() => setFiltroAtivo('aprovados')}
            className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
              filtroAtivo === 'aprovados' 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200 hover:border-green-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aprovados pela Qualidade</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{recebimentosAprovados.length}</p>
              </div>
              <div className={`p-3 rounded-lg ${
                filtroAtivo === 'aprovados' ? 'bg-green-500' : 'bg-green-100'
              }`}>
                <CheckCircle className={`h-6 w-6 ${
                  filtroAtivo === 'aprovados' ? 'text-white' : 'text-green-600'
                }`} />
              </div>
            </div>
          </div>

          {/* Card Recusados */}
          <div 
            onClick={() => setFiltroAtivo('recusados')}
            className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
              filtroAtivo === 'recusados' 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-200 hover:border-red-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Recusados pela Qualidade</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{recebimentosRecusados.length}</p>
              </div>
              <div className={`p-3 rounded-lg ${
                filtroAtivo === 'recusados' ? 'bg-red-500' : 'bg-red-100'
              }`}>
                <XCircle className={`h-6 w-6 ${
                  filtroAtivo === 'recusados' ? 'text-white' : 'text-red-600'
                }`} />
              </div>
            </div>
          </div>

          {/* ✅ NOVO: Card Aprovados Diretamente */}
          <div 
            onClick={() => setFiltroAtivo('aprovados_diretamente')}
            className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
              filtroAtivo === 'aprovados_diretamente' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aprovados Diretamente</p>
                <p className="text-xs text-gray-500 mb-1">Sem análise da qualidade</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{recebimentosAprovadosDiretamente.length}</p>
              </div>
              <div className={`p-3 rounded-lg ${
                filtroAtivo === 'aprovados_diretamente' ? 'bg-blue-500' : 'bg-blue-100'
              }`}>
                <Package className={`h-6 w-6 ${
                  filtroAtivo === 'aprovados_diretamente' ? 'text-white' : 'text-blue-600'
                }`} />
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Recebimentos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ClipboardCheck className="h-5 w-5 mr-2" />
              {getTituloLista()}
            </h2>
          </div>
          <div className="p-6">
            {getRecebimentosFiltrados().length === 0 ? (
              <div className="text-center py-12">
                {filtroAtivo === 'pendentes' ? (
                  <>
                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum recebimento pendente</h3>
                    <p className="text-gray-600">Todos os recebimentos foram analisados</p>
                  </>
                ) : filtroAtivo === 'aprovados' ? (
                  <>
                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum recebimento aprovado</h3>
                    <p className="text-gray-600">Ainda não há recebimentos aprovados pela qualidade</p>
                  </>
                ) : filtroAtivo === 'recusados' ? (
                  <>
                    <XCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum recebimento recusado</h3>
                    <p className="text-gray-600">Ainda não há recebimentos recusados pela qualidade</p>
                  </>
                ) : (
                  <>
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum recebimento aprovado diretamente</h3>
                    <p className="text-gray-600">Ainda não há recebimentos aprovados sem passar pela qualidade</p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {getRecebimentosFiltrados().map((recebimento) => (
                  <div key={recebimento.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{recebimento.produto_nome}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Fornecedor: {recebimento.fornecedor} | Lote(s): {formatarLotesParaExibicao(
                            Array.isArray(recebimento.lote_fornecedor) 
                              ? recebimento.lote_fornecedor 
                              : recebimento.lote_fornecedor
                          )}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Quantidade: {formatQuantidadeComUnidade(recebimento.quantidade_recebida, recebimento.unidade_medida)}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          Recebido em: {formatDate(recebimento.criado_em)}
                        </p>
                      </div>
                      {/* ✅ NOVO: Badge de status dinâmico */}
                      {(() => {
                        const decisao = recebimento.decisao_qualidade?.decisao || recebimento.decisao_final;
                        if (decisao === 'aprovado_qualidade') {
                          return (
                            <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                              Aprovado
                            </span>
                          );
                        } else if (decisao === 'rejeitado_qualidade') {
                          return (
                            <span className="bg-red-100 text-red-800 text-xs font-medium px-3 py-1 rounded-full">
                              Recusado
                            </span>
                          );
                        } else if (decisao === 'aceitar' && !recebimento.decisao_qualidade) {
                          return (
                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">
                              Aprovado Diretamente
                            </span>
                          );
                        } else {
                          return (
                            <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-3 py-1 rounded-full">
                              Em Análise
                            </span>
                          );
                        }
                      })()}
                    </div>

                    {/* Parâmetros Coletados */}
                    {recebimento.parametros_coletados && Object.keys(recebimento.parametros_coletados).length > 0 && (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Parâmetros Coletados:</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(recebimento.parametros_coletados).map(([nome, valor]) => {
                            // ✅ CORREÇÃO: Formatar valor para parâmetros sensoriais (objetos) e numéricos (strings/números)
                            let valorFormatado = 'N/A';
                            if (valor !== null && valor !== undefined) {
                              if (typeof valor === 'object' && valor.conforme !== undefined) {
                                // Parâmetro sensorial
                                if (valor.conforme === true) {
                                  valorFormatado = '✓ Conforme';
                                } else if (valor.descricao) {
                                  valorFormatado = valor.descricao;
                                } else {
                                  valorFormatado = 'Não conforme';
                                }
                              } else {
                                // Parâmetro numérico (string ou número)
                                valorFormatado = String(valor);
                              }
                            }
                            
                            return (
                              <div key={nome} className="text-sm">
                                <span className="text-gray-600">{nome}:</span>
                                <span className="ml-2 font-medium text-gray-900">{valorFormatado}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Conformidade */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${
                          (recebimento.analise_conformidade?.percentual_conformidade || 0) >= 90 ? 'bg-green-500' :
                          (recebimento.analise_conformidade?.percentual_conformidade || 0) >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-700">
                          Conformidade: {recebimento.analise_conformidade?.percentual_conformidade || 0}%
                        </span>
                      </div>
                      {recebimento.analise_conformidade?.status_geral === 'nao_conforme' && (
                        <span className="text-xs text-red-600 font-medium">⚠️ Parâmetros fora dos limites</span>
                      )}
                    </div>

                    {/* Observações do Recebedor */}
                    {recebimento.observacoes_recebedor && (
                      <div className="mb-4 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                        <p className="text-sm font-medium text-blue-900">Observações do Recebedor:</p>
                        <p className="text-sm text-blue-700 mt-1">{recebimento.observacoes_recebedor}</p>
                      </div>
                    )}

                    {/* ✅ NOVO: Informações da Decisão da Qualidade (para aprovados/recusados) */}
                    {(() => {
                      const decisao = recebimento.decisao_qualidade?.decisao || recebimento.decisao_final;
                      const observacoes = recebimento.decisao_qualidade?.observacoes || recebimento.observacoes_qualidade;
                      
                      if (decisao === 'aprovado_qualidade' || decisao === 'rejeitado_qualidade') {
                        return (
                          <div className={`mb-4 p-3 rounded border-l-4 ${
                            decisao === 'aprovado_qualidade' 
                              ? 'bg-green-50 border-green-500' 
                              : 'bg-red-50 border-red-500'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              {decisao === 'aprovado_qualidade' ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-600" />
                              )}
                              <p className={`text-sm font-medium ${
                                decisao === 'aprovado_qualidade' ? 'text-green-900' : 'text-red-900'
                              }`}>
                                Decisão da Qualidade: {decisao === 'aprovado_qualidade' ? 'APROVADO' : 'RECUSADO'}
                              </p>
                            </div>
                            {observacoes && (
                              <p className={`text-sm mt-1 ${
                                decisao === 'aprovado_qualidade' ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {observacoes}
                              </p>
                            )}
                            {recebimento.decisao_qualidade?.decidido_em && (
                              <p className={`text-xs mt-2 ${
                                decisao === 'aprovado_qualidade' ? 'text-green-600' : 'text-red-600'
                              }`}>
                                Decidido em: {formatDate(recebimento.decisao_qualidade.decidido_em)}
                              </p>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Fotos e Vídeos */}
                    {(recebimento.fotos?.length > 0 || recebimento.videos?.length > 0) && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Documentação:</h4>
                        
                        {/* Fotos */}
                        {recebimento.fotos?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-600 mb-2">Fotos ({recebimento.fotos.length})</p>
                            <div className="grid grid-cols-4 gap-2">
                              {recebimento.fotos.map((foto, index) => (
                                <div 
                                  key={index} 
                                  className="relative group cursor-pointer"
                                  onClick={() => openMediaViewer(foto, 'photo')}
                                >
                                  <img
                                    src={typeof foto === 'string' ? foto : (foto?.url || foto)}
                                    alt={`Foto ${index + 1}`}
                                    className="w-full h-20 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                                    onError={(e) => {
                                      console.error('Erro ao carregar thumbnail da foto:', foto);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                    <Maximize2 className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Vídeos */}
                        {recebimento.videos?.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-600 mb-2">Vídeos ({recebimento.videos.length})</p>
                            <div className="grid grid-cols-4 gap-2">
                              {recebimento.videos.map((video, index) => (
                                <div 
                                  key={index} 
                                  className="relative group cursor-pointer"
                                  onClick={() => openMediaViewer(video, 'video')}
                                >
                                  <div className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                                    {(video.thumbnailUrl || (typeof video === 'string' ? false : video?.url)) ? (
                                      <img
                                        src={typeof video === 'string' ? video : (video.thumbnailUrl || video.url || video)}
                                        alt={`Thumbnail ${index + 1}`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          console.error('Erro ao carregar thumbnail do vídeo:', video);
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                        <Video className="w-6 h-6 text-gray-400" />
                                      </div>
                                    )}
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                      <div className="bg-white bg-opacity-90 rounded-full p-1">
                                        <div className="w-0 h-0 border-l-[6px] border-l-blue-600 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                    <Maximize2 className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ✅ NOVO: Botão de ação dinâmico baseado no status */}
                    {(() => {
                      const decisao = recebimento.decisao_qualidade?.decisao || recebimento.decisao_final;
                      
                      if (decisao === 'em_analise_qualidade' || !decisao) {
                        // Pendente - mostrar botão de analisar
                        return (
                          <button
                            onClick={() => handleAbrirDecisao(recebimento)}
                            className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                          >
                            Analisar e Decidir
                          </button>
                        );
                      } else if (decisao === 'aceitar' && !recebimento.decisao_qualidade) {
                        // Aprovado diretamente - mostrar botão de ver detalhes
                        return (
                          <button
                            onClick={() => handleAbrirDecisao(recebimento)}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                          >
                            Ver Detalhes
                          </button>
                        );
                      } else {
                        // Aprovado ou Recusado pela qualidade - mostrar botão de ver detalhes
                        return (
                          <button
                            onClick={() => handleAbrirDecisao(recebimento)}
                            className={`w-full py-3 rounded-lg transition-colors font-medium ${
                              decisao === 'aprovado_qualidade'
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            Ver Detalhes da Decisão
                          </button>
                        );
                      }
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal de Decisão da Qualidade */}
        {showDecisionModal && selectedReceiving && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Decisão da Qualidade</h2>
                <p className="text-sm text-gray-600 mt-1">{selectedReceiving.produto_nome}</p>
              </div>

              <div className="p-6 space-y-6">
                {/* Informações do Recebimento */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div><span className="font-medium">Fornecedor:</span> {selectedReceiving.fornecedor}</div>
                  <div>
                    <span className="font-medium">Lote(s):</span> {formatarLotesParaExibicao(
                      Array.isArray(selectedReceiving.lote_fornecedor) 
                        ? selectedReceiving.lote_fornecedor 
                        : selectedReceiving.lote_fornecedor
                    )}
                  </div>
                  <div><span className="font-medium">Quantidade:</span> {formatQuantidadeComUnidade(selectedReceiving.quantidade_recebida, selectedReceiving.unidade_medida)}</div>
                  <div><span className="font-medium">Conformidade:</span> {selectedReceiving.analise_conformidade?.percentual_conformidade || 0}%</div>
                </div>

                {/* Parâmetros Analisados */}
                {selectedReceiving.analise_conformidade?.parametros_analisados && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Parâmetros Analisados:</h3>
                    <div className="space-y-4">
                      {selectedReceiving.analise_conformidade.parametros_analisados.map((param, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">{param.nome}</h4>
                            {param.status && (
                              <div className={`flex items-center gap-2 ${
                                param.status === 'conforme' ? 'text-green-600' :
                                param.status === 'atencao' ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {param.status === 'conforme' && <CheckCircle className="w-5 h-5" />}
                                {param.status === 'atencao' && <AlertTriangle className="w-5 h-5" />}
                                {param.status === 'nao_conforme' && <XCircle className="w-5 h-5" />}
                              </div>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Valor Coletado:</span>
                              <div className="font-medium">
                                {(() => {
                                  const valor = param.valor_coletado;
                                  if (valor === null || valor === undefined) return 'N/A';
                                  // ✅ CORREÇÃO: Formatar valor para parâmetros sensoriais (objetos)
                                  if (typeof valor === 'object' && valor.conforme !== undefined) {
                                    if (valor.conforme === true) {
                                      return '✓ Conforme';
                                    } else if (valor.descricao) {
                                      return valor.descricao;
                                    }
                                    return 'Não conforme';
                                  }
                                  // Parâmetro numérico
                                  return `${valor} ${param.unidade || ''}`.trim();
                                })()}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">Limite Mínimo:</span>
                              <div className="font-medium">
                                {(param.valor_minimo ?? param.limite_minimo) !== null && (param.valor_minimo ?? param.limite_minimo) !== undefined 
                                  ? (param.valor_minimo ?? param.limite_minimo) 
                                  : 'N/A'}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">Limite Máximo:</span>
                              <div className="font-medium">
                                {(param.valor_maximo ?? param.limite_maximo) !== null && (param.valor_maximo ?? param.limite_maximo) !== undefined 
                                  ? (param.valor_maximo === "Ausente" ? "Ausente (≤ 0)" : (param.valor_maximo ?? param.limite_maximo)) 
                                  : 'N/A'}
                              </div>
                            </div>
                          </div>
                          
                          {param.observacoes && (
                            <div className={`mt-3 text-sm ${
                              param.status === 'conforme' ? 'text-green-600' :
                              param.status === 'atencao' ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {param.observacoes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Observações do Recebedor */}
                {selectedReceiving.observacoes_recebedor && (
                  <div className="p-4 bg-blue-50 rounded border-l-4 border-blue-500">
                    <p className="text-sm font-medium text-blue-900">Observações do Recebedor:</p>
                    <p className="text-sm text-blue-700 mt-1">{selectedReceiving.observacoes_recebedor}</p>
                  </div>
                )}

                {/* Fotos e Vídeos */}
                {(selectedReceiving.fotos?.length > 0 || selectedReceiving.videos?.length > 0) && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Documentação:</h3>
                    
                    {/* Fotos */}
                    {selectedReceiving.fotos?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Fotos ({selectedReceiving.fotos.length})</p>
                        <div className="grid grid-cols-4 gap-2">
                          {selectedReceiving.fotos.map((foto, index) => (
                            <div 
                              key={index} 
                              className="relative group cursor-pointer"
                              onClick={() => openMediaViewer(foto, 'photo')}
                            >
                              <img
                                src={typeof foto === 'string' ? foto : (foto?.url || foto)}
                                alt={`Foto ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                                onError={(e) => {
                                  console.error('Erro ao carregar thumbnail da foto:', foto);
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                <Maximize2 className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Vídeos */}
                    {selectedReceiving.videos?.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Vídeos ({selectedReceiving.videos.length})</p>
                        <div className="grid grid-cols-4 gap-2">
                          {selectedReceiving.videos.map((video, index) => (
                            <div 
                              key={index} 
                              className="relative group cursor-pointer"
                              onClick={() => openMediaViewer(video, 'video')}
                            >
                              <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                                {(video.thumbnailUrl || (typeof video === 'string' ? false : video?.url)) ? (
                                  <img
                                    src={typeof video === 'string' ? video : (video.thumbnailUrl || video.url || video)}
                                    alt={`Thumbnail ${index + 1}`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      console.error('Erro ao carregar thumbnail do vídeo:', video);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                    <Video className="w-8 h-8 text-gray-400" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                  <div className="bg-white bg-opacity-90 rounded-full p-2">
                                    <div className="w-0 h-0 border-l-[8px] border-l-blue-600 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-1"></div>
                                  </div>
                                </div>
                              </div>
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                                <Maximize2 className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Decisão da Qualidade */}
                {/* ✅ CORREÇÃO: Verificar se já existe uma decisão antes de mostrar os botões */}
                {(() => {
                  // Verificar se já existe decisão: decisao_qualidade.decisao ou decisao_final
                  // ✅ NOVO: Verificar também se foi aprovado diretamente
                  const decisaoExistente = selectedReceiving.decisao_qualidade?.decisao || 
                                          (selectedReceiving.decisao_final === 'aprovado_qualidade' ? 'aprovado_qualidade' : 
                                           selectedReceiving.decisao_final === 'rejeitado_qualidade' ? 'rejeitado_qualidade' : 
                                           (selectedReceiving.decisao_final === 'aceitar' && !selectedReceiving.decisao_qualidade) ? 'aprovado_diretamente' : null);
                  
                  if (decisaoExistente === 'aprovado_diretamente') {
                    // Aprovado diretamente - mostrar informação especial
                    return (
                      <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-900">Aprovado Diretamente</span>
                        </div>
                        <div className="text-sm text-blue-700">
                          <p>Este recebimento foi aprovado diretamente no momento do recebimento, sem passar pela análise da qualidade.</p>
                          {selectedReceiving.decisao_final_recebedor?.observacoes && (
                            <div className="mt-2">
                              <span className="font-medium">Observações do Recebedor: </span>
                              <span>{selectedReceiving.decisao_final_recebedor.observacoes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return decisaoExistente ? (
                    <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">Decisão já registrada</span>
                      </div>
                      <div className="text-sm text-blue-700">
                        <div className="mb-1">
                          <span className="font-medium">Decisão: </span>
                          <span className={`font-semibold ${
                            decisaoExistente === 'aprovado_qualidade'
                              ? 'text-green-700'
                              : 'text-red-700'
                          }`}>
                            {decisaoExistente === 'aprovado_qualidade'
                              ? 'APROVADO'
                              : 'REJEITADO'}
                          </span>
                        </div>
                        {selectedReceiving.decisao_qualidade?.observacoes || selectedReceiving.observacoes_qualidade ? (
                          <div className="mt-2">
                            <span className="font-medium">Observações: </span>
                            <span>{selectedReceiving.decisao_qualidade?.observacoes || selectedReceiving.observacoes_qualidade}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Decisão da Qualidade: *
                    </label>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        onClick={() => setDecisaoQualidade('aprovado_qualidade')}
                        disabled={processando}
                        className={`p-6 border-2 rounded-lg transition-all group ${
                          processando
                            ? 'border-green-200 opacity-50 cursor-wait'
                            : decisaoQualidade === 'aprovado_qualidade'
                              ? 'border-green-500 bg-green-50'
                              : 'border-green-200 hover:border-green-500 hover:bg-green-50 cursor-pointer'
                        }`}
                      >
                        <div className="text-center">
                          <CheckCircle className={`w-12 h-12 mx-auto mb-3 transition-transform ${
                            decisaoQualidade === 'aprovado_qualidade' ? 'text-green-500' : 'text-green-500 group-hover:scale-110'
                          }`} />
                          <div className="font-medium text-green-700 mb-2">APROVAR</div>
                          <div className="text-sm text-gray-600">
                            Liberar para decisão final do recebedor
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setDecisaoQualidade('rejeitado_qualidade')}
                        disabled={processando}
                        className={`p-6 border-2 rounded-lg transition-all group ${
                          processando
                            ? 'border-red-200 opacity-50 cursor-wait'
                            : decisaoQualidade === 'rejeitado_qualidade'
                              ? 'border-red-500 bg-red-50'
                              : 'border-red-200 hover:border-red-500 hover:bg-red-50 cursor-pointer'
                        }`}
                      >
                        <div className="text-center">
                          <XCircle className={`w-12 h-12 mx-auto mb-3 transition-transform ${
                            decisaoQualidade === 'rejeitado_qualidade' ? 'text-red-500' : 'text-red-500 group-hover:scale-110'
                          }`} />
                          <div className="font-medium text-red-700 mb-2">REJEITAR</div>
                          <div className="text-sm text-gray-600">
                            Material não conforme - não aceitar
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                  );
                })()}

                {/* Observações da Qualidade */}
                {/* ✅ CORREÇÃO: Só mostrar campo de observações se ainda não foi tomada uma decisão */}
                {(() => {
                  const decisaoExistente = selectedReceiving.decisao_qualidade?.decisao || 
                                          (selectedReceiving.decisao_final === 'aprovado_qualidade' ? 'aprovado_qualidade' : 
                                           selectedReceiving.decisao_final === 'rejeitado_qualidade' ? 'rejeitado_qualidade' : 
                                           (selectedReceiving.decisao_final === 'aceitar' && !selectedReceiving.decisao_qualidade) ? 'aprovado_diretamente' : null);
                  
                  return !decisaoExistente && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observações da Qualidade: {decisaoQualidade === 'rejeitado_qualidade' && '*'}
                    </label>
                    <textarea
                      value={observacoesQualidade}
                      onChange={(e) => setObservacoesQualidade(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Justificativa ou observações sobre a decisão..."
                    />
                    {decisaoQualidade === 'rejeitado_qualidade' && !observacoesQualidade && (
                      <p className="text-xs text-red-600 mt-1">Observações são obrigatórias para rejeição</p>
                    )}
                  </div>
                  );
                })()}
              </div>

              {/* Botões do Modal */}
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDecisionModal(false);
                    setSelectedReceiving(null);
                  }}
                  disabled={processando}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {(() => {
                    const decisaoExistente = selectedReceiving.decisao_qualidade?.decisao || 
                                            (selectedReceiving.decisao_final === 'aprovado_qualidade' ? 'aprovado_qualidade' : 
                                             selectedReceiving.decisao_final === 'rejeitado_qualidade' ? 'rejeitado_qualidade' : 
                                             (selectedReceiving.decisao_final === 'aceitar' && !selectedReceiving.decisao_qualidade) ? 'aprovado_diretamente' : null);
                    return decisaoExistente ? 'Fechar' : 'Cancelar';
                  })()}
                </button>
                {/* ✅ CORREÇÃO: Só mostrar botão de confirmar se ainda não foi tomada uma decisão */}
                {(() => {
                  const decisaoExistente = selectedReceiving.decisao_qualidade?.decisao || 
                                          (selectedReceiving.decisao_final === 'aprovado_qualidade' ? 'aprovado_qualidade' : 
                                           selectedReceiving.decisao_final === 'rejeitado_qualidade' ? 'rejeitado_qualidade' : 
                                           (selectedReceiving.decisao_final === 'aceitar' && !selectedReceiving.decisao_qualidade) ? 'aprovado_diretamente' : null);
                  
                  return !decisaoExistente && (
                  <button
                    onClick={handleDecidirQualidade}
                    disabled={processando || !decisaoQualidade || (decisaoQualidade === 'rejeitado_qualidade' && !observacoesQualidade)}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      processando || !decisaoQualidade || (decisaoQualidade === 'rejeitado_qualidade' && !observacoesQualidade)
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {processando ? 'Processando...' : 'Confirmar Decisão'}
                  </button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Visualização de Mídia */}
        {showMediaModal && viewingMedia && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-7xl w-full max-h-[95vh]">
              <button
                onClick={() => {
                  setShowMediaModal(false);
                  setViewingMedia(null);
                }}
                className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-2 z-10 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              {viewingMedia.type === 'photo' ? (
                <img
                  src={viewingMedia.url || viewingMedia.dataUrl || viewingMedia.thumbnailUrl || (typeof viewingMedia === 'string' ? viewingMedia : '')}
                  alt="Foto"
                  className="max-w-full max-h-[95vh] mx-auto object-contain rounded-lg"
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', viewingMedia);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <video
                  src={viewingMedia.url || viewingMedia.dataUrl || viewingMedia.thumbnailUrl || (typeof viewingMedia === 'string' ? viewingMedia : '')}
                  controls
                  autoPlay
                  className="max-w-full max-h-[95vh] mx-auto rounded-lg"
                  onError={(e) => {
                    console.error('Erro ao carregar vídeo:', viewingMedia);
                  }}
                >
                  Seu navegador não suporta a reprodução de vídeos.
                </video>
              )}
              
              {viewingMedia.name && (
                <div className="mt-4 text-center text-white">
                  <p className="text-sm">{viewingMedia.name}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QualidadeDashboard;

